import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    useGetBlogPostsQuery,
    useGetBlogCategoriesQuery
} from "../../services/blogApi";
import "./BlogList.css";

const BlogList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // URL parametrelerinden değerleri al (SEO için)
    const [selectedCategory, setSelectedCategory] = useState(searchParams.get('kategori') || 'all');
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('sayfa')) || 1);
    const postsPerPage = 6;


    // RTK Query hooks
    const {
        data: postsData,
        isLoading: postsLoading,
        error: postsError,
        refetch: refetchPosts
    } = useGetBlogPostsQuery({
        page: currentPage,
        per_page: postsPerPage,
        category: selectedCategory
    });
    
    const {
        data: categoriesData,
        isLoading: categoriesLoading,
        error: categoriesError
    } = useGetBlogCategoriesQuery();

    // Kategorileri formatla
    const categories = categoriesData?.success ? [
        { key: 'all', label: 'Tümü', count: 0 },
        ...categoriesData.data.map(cat => ({
            key: cat.slug,
            label: cat.name,
            count: cat.count
        }))
    ] : [];

    // Posts ve pagination verilerini al
    const posts = postsData?.success ? postsData.data.posts : [];
    const pagination = postsData?.success ? postsData.data.pagination : {};

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        setCurrentPage(1);

        // URL'yi güncelle (SEO için)
        const newParams = new URLSearchParams();
        if (category !== 'all') {
            newParams.set('kategori', category);
        }
        newParams.set('sayfa', '1');
        setSearchParams(newParams);
    };

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);

        // URL'yi güncelle (SEO için)
        const newParams = new URLSearchParams();
        if (selectedCategory !== 'all') {
            newParams.set('kategori', selectedCategory);
        }
        newParams.set('sayfa', pageNumber.toString());
        setSearchParams(newParams);

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // SEO için dinamik meta veriler
    const getPageTitle = () => {
        if (selectedCategory !== 'all') {
            const categoryName = categories.find(cat => cat.key === selectedCategory)?.label;
            return `${categoryName} Blog Yazıları - Sayfa ${currentPage} | BiKuyum`;
        }
        return currentPage > 1
            ? `Blog - Sayfa ${currentPage} | BiKuyum`
            : 'Blog - Altın ve Mücevher Rehberi | BiKuyum';
    };

    const getPageDescription = () => {
        if (selectedCategory !== 'all') {
            const categoryName = categories.find(cat => cat.key === selectedCategory)?.label;
            return `${categoryName} kategorisindeki blog yazıları. Altın yatırımı, mücevher bakımı ve piyasa analizleri hakkında uzman görüşleri.`;
        }
        return 'Altın yatırımı, ikinci el altın alışverişi, mücevher bakımı ve piyasa analizleri hakkında uzman görüşleri ve rehberler.';
    };

    // Error state
    const error = postsError || categoriesError;
    let errorMessage = 'Bir hata oluştu';

    if (error) {
        if (error.status) {
            errorMessage = `HTTP ${error.status}: `;
            if (error.data?.error) {
                errorMessage += error.data.error;
            } else if (error.data?.message) {
                errorMessage += error.data.message;
            } else {
                errorMessage += 'Sunucu hatası';
            }
        } else if (error.message) {
            errorMessage = error.message;
        } else if (typeof error === 'string') {
            errorMessage = error;
        }
    }

    // Loading state
    if (postsLoading && posts.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{getPageTitle()}</title>
                <meta name="description" content={getPageDescription()} />
                <meta name="keywords" content="altın blog, altın yatırımı, ikinci el altın, mücevher rehberi, altın piyasası, takı bakımı, altın analizi" />
                <meta property="og:title" content={getPageTitle()} />
                <meta property="og:description" content={getPageDescription()} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://bikuyum.com/blog${window.location.search}`} />
                <link rel="canonical" href={`https://bikuyum.com/blog${window.location.search}`} />

                {/* Pagination için prev/next linkler */}
                {pagination.has_prev && (
                    <link rel="prev" href={`https://bikuyum.com/blog?sayfa=${currentPage - 1}${selectedCategory !== 'all' ? `&kategori=${selectedCategory}` : ''}`} />
                )}
                {pagination.has_next && (
                    <link rel="next" href={`https://bikuyum.com/blog?sayfa=${currentPage + 1}${selectedCategory !== 'all' ? `&kategori=${selectedCategory}` : ''}`} />
                )}

                {/* JSON-LD Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Blog",
                        "name": "BiKuyum Blog",
                        "description": "Altın ve mücevher dünyası hakkında uzman görüşleri",
                        "url": "https://bikuyum.com/blog",
                        "publisher": {
                            "@type": "Organization",
                            "name": "BiKuyum",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://bikuyum.com/logo.png"
                            }
                        },
                        "blogPost": posts.map(post => ({
                            "@type": "BlogPosting",
                            "headline": post.title,
                            "description": post.excerpt,
                            "url": `https://bikuyum.com/blog/${post.slug}`,
                            "datePublished": post.published_date,
                            "author": {
                                "@type": "Person",
                                "name": "BiKuyum Uzmanı"
                            }
                        }))
                    })}
                </script>
            </Helmet>

            <div className="py-2">
                {/* Breadcrumb */}
                <div className="container">
                    <nav aria-label="breadcrumb" className="mb-4">
                        <ol className="breadcrumb breadcrumb-custom">
                            <li className="breadcrumb-item">
                                <Link to="/">Ana Sayfa</Link>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                Blog
                                {selectedCategory !== 'all' && categories.length > 0 && (
                                    <span> -{categories.find(cat => cat.key === selectedCategory)?.label || selectedCategory}</span>
                                )}
                                {currentPage > 1 && (
                                    <span> - Sayfa {currentPage}</span>
                                )}
                            </li>
                        </ol>
                    </nav>
                </div>

                <div className="container blog-section">
                    {/* Header */}
                    <div className="row">
                        <div className="col-12 text-center mb-5">
                            <h1 className="display-4 fw-bold text-dark mb-3">
                                {selectedCategory !== 'all'
                                    ? `${categories.find(cat => cat.key === selectedCategory)?.label || selectedCategory} Blog Yazıları`
                                    : 'Blog'
                                }
                            </h1>
                            <p className="lead text-muted">
                                {posts.length > 0 
                                    ? 'Uzman görüşleri, piyasa analizleri ve değerli tavsiyeler'
                                    : selectedCategory !== 'all'
                                        ? `${categories.find(cat => cat.key === selectedCategory)?.label || selectedCategory} kategorisinde arama yapılıyor`
                                        : 'Uzman görüşleri, piyasa analizleri ve değerli tavsiyeler'
                                }
                            </p>
                        </div>
                    </div>

                    {/* Category Filter - Sadece blog yazısı varsa göster */}
                    {categories.length > 1 && !categoriesLoading && posts.length > 0 && (
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="d-flex flex-wrap justify-content-center gap-2">
                                    {categories.map((category) => (
                                        <button
                                            key={category.key}
                                            className={`btn ${selectedCategory === category.key ? 'btn-bikuyum' : 'btn-outline-bikuyum'} btn-sm`}
                                            onClick={() => handleCategoryChange(category.key)}
                                            disabled={postsLoading}
                                        >
                                            {category.label}
                                            {category.count > 0 && (
                                                <span className="ms-1">({category.count})</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Blog Posts Grid */}
                    {posts.length > 0 ? (
                        <div className="row g-4 px-md-5 px-1">
                            {posts.map((post) => (
                                <div key={post.id} className="col-lg-4 col-md-6">
                                    <article
                                        className="blog-card h-100 shadow-sm border-0 rounded-3 overflow-hidden"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/blog/${post.slug}`)}
                                    >
                                        <div className="blog-image-container position-relative">
                                            <img
                                                src={post.image_url}
                                                alt={post.title}
                                                className="blog-image w-100"
                                                style={{
                                                    height: '250px',
                                                    objectFit: 'cover',
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                                onError={(e) => {
                                                    e.target.style.backgroundColor = '#e9ecef';
                                                    e.target.style.display = 'flex';
                                                    e.target.style.alignItems = 'center';
                                                    e.target.style.justifyContent = 'center';
                                                    e.target.innerHTML = '<i class="fas fa-image text-muted fa-2x"></i>';
                                                }}
                                            />
                                            <div className="blog-tags position-absolute top-0 start-0 m-3">
                                                {post.tags && post.tags.slice(0, 2).map((tag, tagIndex) => (
                                                    <span
                                                        key={tagIndex}
                                                        className="badge bg-primary me-1 mb-1"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="card-body p-4">
                                            <div className="blog-meta d-flex justify-content-between align-items-center mb-3">
                                                <small className="text-muted">
                                                    <i className="far fa-calendar-alt me-1"></i>
                                                    {formatDate(post.published_date)}
                                                </small>
                                                <small className="text-muted">
                                                    <i className="far fa-clock me-1"></i>
                                                    {post.reading_time} dk
                                                </small>
                                            </div>

                                            <h2 className="blog-title h5 fw-bold mb-3">
                                                <Link
                                                    to={`/blog/${post.slug}`}
                                                    className="text-decoration-none text-dark"
                                                >
                                                    {post.title}
                                                </Link>
                                            </h2>

                                            <p className="blog-excerpt text-muted mb-3">
                                                {post.excerpt}
                                            </p>

                                            <div className="blog-author d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center">
                                                    <div className="author-avatar bg-primary rounded-circle d-flex align-items-center justify-content-center me-2"
                                                        style={{ width: '32px', height: '32px' }}>
                                                        <i className="fas fa-user text-white small"></i>
                                                    </div>
                                                    <small className="text-muted">BiKuyum Uzmanı</small>
                                                </div>
                                                {post.view_count > 0 && (
                                                    <small className="text-muted">
                                                        <i className="far fa-eye me-1"></i>
                                                        {post.view_count}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </article>
                                </div>
                            ))}
                        </div>
                    ) : !postsLoading && !error && (
                        /* No Results */
                        <div className="row">
                            <div className="col-12 text-center py-5">
                                <i className="fas fa-search fa-3x text-muted mb-3"></i>
                                <h3 className="text-muted">
                                    {selectedCategory !== 'all' 
                                        ? `${categories.find(cat => cat.key === selectedCategory)?.label || selectedCategory} kategorisinde blog yazısı bulunamadı`
                                        : 'Henüz blog yazısı bulunmuyor'
                                    }
                                </h3>
                                <p className="text-muted">
                                    {selectedCategory !== 'all' 
                                        ? 'Bu kategoride henüz içerik bulunmuyor. Diğer kategorileri kontrol edebilir veya ana blog sayfasına dönebilirsiniz.'
                                        : 'Yakında değerli içeriklerle burada olacağız. Takipte kalın!'
                                    }
                                </p>
                                {selectedCategory !== 'all' && (
                                    <div className="mt-3">
                                        <button 
                                            className="btn btn-outline-bikuyum me-2"
                                            onClick={() => handleCategoryChange('all')}
                                        >
                                            <i className="fas fa-arrow-left me-1"></i>
                                            Tüm Kategorileri Görüntüle
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {pagination.total_pages > 1 && (
                        <div className="row mt-5">
                            <div className="col-12">
                                <nav aria-label="Blog pagination">
                                    <ul className="pagination justify-content-center">
                                        <li className={`page-item ${!pagination.has_prev ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => paginate(currentPage - 1)}
                                                disabled={!pagination.has_prev || postsLoading}
                                                aria-label="Önceki sayfa"
                                            >
                                                <i className="fas fa-chevron-left"></i>
                                            </button>
                                        </li>

                                        {[...Array(pagination.total_pages)].map((_, index) => (
                                            <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => paginate(index + 1)}
                                                    disabled={postsLoading}
                                                    aria-label={`Sayfa ${index + 1}`}
                                                >
                                                    {index + 1}
                                                </button>
                                            </li>
                                        ))}

                                        <li className={`page-item ${!pagination.has_next ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => paginate(currentPage + 1)}
                                                disabled={!pagination.has_next || postsLoading}
                                                aria-label="Sonraki sayfa"
                                            >
                                                <i className="fas fa-chevron-right"></i>
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>
                    )}

                    {/* Loading indicator for pagination */}
                    {postsLoading && posts.length > 0 && (
                        <div className="row mt-3">
                            <div className="col-12 text-center">
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Yükleniyor...</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BlogList;